import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PatientsFilesApiService } from '@troyai/patients/data-access';
import { DocumentsApiService } from '@troyai/portal/documents/data-access';
import { LoadingState, getLoadingStatus } from '@troyai/shared/state';
import { catchError, forkJoin, from, map, mergeMap, of, switchMap, tap, throwError } from 'rxjs';
import { ClinicalObservationsApiService } from '../clinical-observations-api.service';
import { ClinicalObservationsLocalStorageService } from '../clinical-observations-ls.service';
import { ClinicalObservationsHelperService } from '../clinical-observations.service';
import {
  ClinicalObservation,
  FormattedClinicalObservation,
} from '../models/clinical-observation.interface';
import { ClinicalObservationsStateModel } from './clinical-observations-state.model';
import {
  AddNewClinicalObservation,
  AssociateMemberFilesToClinicalObservation,
  EditClinicalObservation,
  EmptySelectedClinicalObservation,
  GetPatientClinicalObservationGroups,
  GetPatientClinicalObservations,
  GetSinglePatientClinicalObservation,
  SearchStandardMedicalCode,
  ToggleClinicalObservationGroupExpanded,
  ToggleClinicalObservationGroupPinned,
} from './clinical-observations.actions';

@State<ClinicalObservationsStateModel>({
  name: 'clinicalObservationsState',
  defaults: {
    clinicalObservationsList: {
      result: null,
      loadingStatus: LoadingState.INITIAL,
    },
    clinicalObservationGroups: {
      result: null,
      loadingStatus: LoadingState.INITIAL,
    },
    selectedClinicalObservation: {
      result: null,
      loadingStatus: LoadingState.INITIAL,
    },
    medicalCodeSearch: {
      result: null,
      loadingStatus: LoadingState.INITIAL,
    },
  },
})
@Injectable()
export class ClinicalObservationsState {
  private clinicalObservationsApiService = inject(ClinicalObservationsApiService);
  private patientsFileApiService = inject(PatientsFilesApiService);
  private documentsApiService = inject(DocumentsApiService);
  private clinicalObsHelperService = inject(ClinicalObservationsHelperService);
  private clinicalObsLocalStorageService = inject(ClinicalObservationsLocalStorageService);

  @Selector()
  static clinicalObservationsList(state: ClinicalObservationsStateModel) {
    return state.clinicalObservationsList.result;
  }

  @Selector()
  static clinicalObservationsListLoadingStatus(state: ClinicalObservationsStateModel) {
    return getLoadingStatus(state.clinicalObservationsList.loadingStatus);
  }

  @Selector()
  static clinicalObservationGroups(state: ClinicalObservationsStateModel) {
    return state.clinicalObservationGroups.result;
  }

  @Selector()
  static clinicalObservationGroupsLoadingStatus(state: ClinicalObservationsStateModel) {
    return getLoadingStatus(state.clinicalObservationGroups.loadingStatus);
  }

  @Selector()
  static selectedClinicalObservation(state: ClinicalObservationsStateModel) {
    return state.selectedClinicalObservation.result;
  }

  @Selector()
  static selectedClinicalObservationLoadingStatus(state: ClinicalObservationsStateModel) {
    return getLoadingStatus(state.selectedClinicalObservation.loadingStatus);
  }

  @Selector()
  static lastUpdateClinicalObservationsList(state: ClinicalObservationsStateModel) {
    if (!state.clinicalObservationsList) {
      return null;
    }

    const clinicalObservationsList = state.clinicalObservationsList.result;
    if (!clinicalObservationsList) {
      return null;
    }

    return clinicalObservationsList.reduce((acc: ClinicalObservation | null, clinicalObs) => {
      if (!acc || clinicalObs.recorded_at > acc.recorded_at) {
        return clinicalObs;
      }
      return acc;
    }, null);
  }

  @Selector()
  static standardMedicalCodeSearch(state: ClinicalObservationsStateModel) {
    return state.medicalCodeSearch.result;
  }

  @Selector()
  static standardMedicalCodeSearchLoadingStatus(state: ClinicalObservationsStateModel) {
    return getLoadingStatus(state.medicalCodeSearch.loadingStatus);
  }

  @Selector()
  static standardMedicalCodeSearchError(state: ClinicalObservationsStateModel) {
    return state.medicalCodeSearch.error;
  }

  @Action(GetPatientClinicalObservationGroups)
  getPatientClinicalObservationGroups(
    { patchState }: StateContext<ClinicalObservationsStateModel>,
    action: GetPatientClinicalObservationGroups
  ) {
    patchState({
      clinicalObservationGroups: {
        result: [],
        loadingStatus: LoadingState.LOADING,
      },
    });

    if (action.payload.globalId) {
      return this.clinicalObservationsApiService
        .getClinicalObservations(action.payload.globalId)
        .pipe(
          tap((items) => {
            patchState({
              clinicalObservationsList: {
                result: items,
                loadingStatus: LoadingState.LOADED,
              },
            });
          }),
          map((items) => this.clinicalObsHelperService.formatClinicalObservations(items || [])),
          map((formattedItems: FormattedClinicalObservation[]) => {
            return this.clinicalObsHelperService.groupFormattedClinicalObservations(formattedItems);
          }),
          tap((clinicalObsGroups) =>
            patchState({
              clinicalObservationGroups: {
                result: clinicalObsGroups,
                loadingStatus: LoadingState.LOADED,
              },
            })
          )
        );
    } else {
      return;
    }
  }

  @Action(ToggleClinicalObservationGroupExpanded)
  toggleClinicalObservationGroupExpanded(
    { getState, patchState }: StateContext<ClinicalObservationsStateModel>,
    action: ToggleClinicalObservationGroupExpanded
  ) {
    const { clinicalObservationGroups: clinicalObservationGroups } = getState();

    if (!clinicalObservationGroups.result) {
      return;
    }

    patchState({
      clinicalObservationGroups: {
        result: clinicalObservationGroups.result.map((group) => {
          if (group.type === action.payload.groupName) {
            return {
              ...group,
              isExpanded: action.payload.isExpanded,
            };
          }
          return group;
        }),
        loadingStatus: LoadingState.LOADED,
      },
    });
  }

  @Action(ToggleClinicalObservationGroupPinned)
  toggleClinicalObservationGroupPinned(
    { getState, patchState }: StateContext<ClinicalObservationsStateModel>,
    action: ToggleClinicalObservationGroupPinned
  ) {
    const { clinicalObservationGroups: clinicalObservationGroups } = getState();

    if (!clinicalObservationGroups.result) {
      return;
    }

    this.clinicalObsLocalStorageService.togglePinnedGroup(action.payload.groupName);

    patchState({
      clinicalObservationGroups: {
        result: clinicalObservationGroups.result.map((group) => {
          if (group.type === action.payload.groupName) {
            return {
              ...group,
              isPinned: action.payload.isPinned,
            };
          }
          return group;
        }),
        loadingStatus: LoadingState.LOADED,
      },
    });
  }

  @Action(GetPatientClinicalObservations)
  getPatientClinicalObservations(
    { patchState }: StateContext<ClinicalObservationsStateModel>,
    action: GetPatientClinicalObservations
  ) {
    patchState({
      clinicalObservationsList: {
        result: [],
        loadingStatus: LoadingState.LOADING,
      },
    });

    if (action.payload.globalId) {
      return this.clinicalObservationsApiService
        .getClinicalObservations(action.payload.globalId)
        .pipe(
          tap((clinicalObs) =>
            patchState({
              clinicalObservationsList: {
                result: clinicalObs,
                loadingStatus: LoadingState.LOADED,
              },
            })
          )
        );
    } else {
      return;
    }
  }

  @Action(AddNewClinicalObservation)
  addNewClinicalObservation(
    { patchState, getState }: StateContext<ClinicalObservationsStateModel>,
    action: AddNewClinicalObservation
  ) {
    patchState({
      selectedClinicalObservation: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    const { clinicalObservationsList: clinicalObservationsList } = getState();

    return this.clinicalObservationsApiService
      .addNewClinicalObservation(action.memberGlobalId, [action.payload])
      .pipe(
        tap((clinicalObs) => {
          patchState({
            selectedClinicalObservation: {
              result: clinicalObs,
              loadingStatus: LoadingState.LOADED,
            },
          });
        }),
        map((clinicalObs) => {
          if (!clinicalObservationsList.result) return;

          return [...clinicalObservationsList.result, clinicalObs];
        }),
        tap((items) => {
          if (!items) return;
          patchState({
            clinicalObservationsList: {
              result: items,
              loadingStatus: LoadingState.LOADED,
            },
          });
        }),
        map((items) => this.clinicalObsHelperService.formatClinicalObservations(items || [])),
        map((formattedItems: FormattedClinicalObservation[]) => {
          return this.clinicalObsHelperService.groupFormattedClinicalObservations(formattedItems);
        }),
        tap((clinicalObsGroups) =>
          patchState({
            clinicalObservationGroups: {
              result: clinicalObsGroups,
              loadingStatus: LoadingState.LOADED,
            },
          })
        )
      );
  }

  @Action(EditClinicalObservation)
  editClinicalObservation(
    { patchState, getState }: StateContext<ClinicalObservationsStateModel>,
    action: EditClinicalObservation
  ) {
    const {
      clinicalObservationsList: clinicalObservationsList,
      selectedClinicalObservation: selectedClinicalObservation,
    } = getState();
    const currentClinicalObservation = selectedClinicalObservation.result;

    // Nothing to edit as no selected clinical observation exists in state
    if (!currentClinicalObservation) {
      return;
    }

    patchState({
      selectedClinicalObservation: {
        result: currentClinicalObservation,
        loadingStatus: LoadingState.LOADING,
      },
    });

    return this.clinicalObservationsApiService
      .editClinicalObservation(action.memberGlobalId, currentClinicalObservation.id, action.payload)
      .pipe(
        switchMap(() => {
          return this.clinicalObservationsApiService
            .getSingleClinicalObservation(action.memberGlobalId, currentClinicalObservation.id)
            .pipe(
              tap((updatedClinicalObs) => {
                if (!clinicalObservationsList.result) return;

                const updatedClinicalObservationsList = clinicalObservationsList.result.map(
                  (item) => {
                    if (item.id === currentClinicalObservation.id) {
                      return updatedClinicalObs;
                    }

                    return item;
                  }
                );

                patchState({
                  selectedClinicalObservation: {
                    result: updatedClinicalObs,
                    loadingStatus: LoadingState.LOADED,
                  },
                  clinicalObservationsList: {
                    result: updatedClinicalObservationsList,
                    loadingStatus: LoadingState.LOADED,
                  },
                });
              })
            );
        })
      );
  }

  @Action(GetSinglePatientClinicalObservation)
  getSinglePatientClinicalObservation(
    { patchState }: StateContext<ClinicalObservationsStateModel>,
    action: GetSinglePatientClinicalObservation
  ) {
    patchState({
      selectedClinicalObservation: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    return this.clinicalObservationsApiService
      .getSingleClinicalObservation(
        action.payload.memberGlobalId,
        action.payload.clinicalObservationId
      )
      .pipe(
        tap((clinicalObs) => {
          patchState({
            selectedClinicalObservation: {
              result: clinicalObs,
              loadingStatus: LoadingState.LOADED,
            },
          });
        }),
        mergeMap((clinicalObs) =>
          // Retrieve all the signed urls for the files in parallel
          forkJoin(
            clinicalObs.files.map((file) =>
              this.documentsApiService.getSingleDocument(action.payload.memberGlobalId, file.id)
            )
          ).pipe(
            map((files) => {
              return {
                ...clinicalObs,
                files: clinicalObs.files.map((file, index) => ({
                  ...file,
                  signed_url: files[index].url,
                  file_extension: files[index].file_extension,
                })),
              };
            })
          )
        ),
        tap((clinicalObs) => {
          patchState({
            selectedClinicalObservation: {
              result: clinicalObs,
              loadingStatus: LoadingState.LOADED,
            },
          });
        }),
        catchError((err) => {
          patchState({
            selectedClinicalObservation: {
              result: null,
              loadingStatus: LoadingState.ERRORED,
            },
          });
          return throwError(() => err);
        })
      );
  }

  @Action(AssociateMemberFilesToClinicalObservation)
  associateMemberFileToClinicalObservation(
    _: StateContext<ClinicalObservationsStateModel>,
    action: AssociateMemberFilesToClinicalObservation
  ) {
    return from(action.payload.fileIds).pipe(
      mergeMap((fileId: number) => {
        return this.patientsFileApiService.associateFile(
          action.payload.memberGlobalId,
          fileId,
          action.payload.data
        );
      }, 3) // Maximum 3 file uploads at once
    );
  }

  @Action(EmptySelectedClinicalObservation)
  emptyNewClinicalObservationAdding({ patchState }: StateContext<ClinicalObservationsStateModel>) {
    patchState({
      selectedClinicalObservation: {
        result: null,
        loadingStatus: LoadingState.INITIAL,
      },
    });
  }

  @Action(SearchStandardMedicalCode)
  searchStandardMedicalCode(
    { patchState }: StateContext<ClinicalObservationsStateModel>,
    action: SearchStandardMedicalCode
  ) {
    if (!action.code) {
      return patchState({
        medicalCodeSearch: {
          result: null,
          loadingStatus: LoadingState.INITIAL,
        },
      });
    }

    patchState({
      medicalCodeSearch: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    return this.clinicalObservationsApiService.getStandardMedicalCode(action.code).pipe(
      tap((codeDetails) => {
        patchState({
          medicalCodeSearch: {
            result: codeDetails,
            loadingStatus: LoadingState.LOADED,
          },
        });
      }),
      catchError((err) => {
        if (err.status === 404) {
          patchState({
            medicalCodeSearch: {
              result: null,
              loadingStatus: LoadingState.ERRORED,
              error: {
                message: 'Code not found',
                title: 'Code not found',
              },
            },
          });
          return of(null);
        } else {
          patchState({
            medicalCodeSearch: {
              result: null,
              loadingStatus: LoadingState.ERRORED,
            },
          });
          return throwError(() => err);
        }
      })
    );
  }
}
